import { collection, doc, setDoc, updateDoc, getDoc, getDocs, query, orderBy, limit, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import type { CalculatorFormData, CalculatorResults } from '../types';
import { createUserDocumentId, generateSessionId as generateSessionIdUtil } from '../../../utils/crypto';

// Anonymous analytics tracking (no PII)
export interface AnalyticsSession {
  sessionId: string;
  startedAt: Timestamp;
  lastActivity: Timestamp;
  currentStep: number;
  stepsVisited: number[];
  timePerStep: { [step: number]: number };
  industry?: string;
  companySize?: string;
  leadSource?: string;
  completed: boolean;
  userAgent: string;
  referrer: string;
}

// Checkpoint save data (with consent)
export interface SessionCheckpoint {
  sessionId: string;
  savedAt: Timestamp;
  currentStep: number;
  highestStepReached?: number;  // Track the furthest step user has reached
  leadSource?: string;
  isEditSession?: boolean;
  originalReportId?: string;
  contactInfo?: {
    organizationName: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    contactTitle: string;
  };
  formData: Partial<CalculatorFormData>;
  calculations?: Partial<CalculatorResults>;
  consentGiven?: Timestamp;
}


// New user-centric interfaces
export interface UserProfile {
  organizationName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  contactTitle: string;
  firstSeen: Timestamp;
  lastActive: Timestamp;
  totalSessions?: number;
}

export interface CalculatorSession {
  sessionId: string;
  startedAt: Timestamp;
  completedAt?: Timestamp;
  leadSource?: string;
  formData: CalculatorFormData;
  results: CalculatorResults;
  // PDF fields removed - using HTML reports only
  scenarioMode?: 'conservative' | 'moderate' | 'optimistic';
  version: string; // Calculator version
  enhancedData?: {
    year1ROI?: number;
    steadyStateROI?: number;
    paybackPeriod?: number;
    annualSubscription?: number;
    implementationInvestment?: number;
    enhancedSavings?: any;
  };
}


/**
 * Helper function to check if Firebase is properly configured
 */
export const isFirebaseConfigured = (): boolean => {
  const hasApiKey = !!process.env.REACT_APP_FIREBASE_API_KEY;
  const hasProjectId = !!process.env.REACT_APP_FIREBASE_PROJECT_ID;
  
  // Environment check removed - handled silently
  
  return hasApiKey && hasProjectId;
};

/**
 * Generate a unique session ID
 */
export const generateSessionId = (): string => {
  return generateSessionIdUtil();
};

/**
 * Initialize anonymous analytics session
 * @param sessionId Unique session identifier
 * @param leadSource Optional lead source from URL parameter
 * @returns Promise with success status
 */
export const initializeAnalyticsSession = async (sessionId: string, leadSource?: string): Promise<boolean> => {
  try {
    const isConfigured = isFirebaseConfigured();
    
    if (!isConfigured) {
      return false;
    }
    
    const sessionData: AnalyticsSession = {
      sessionId,
      startedAt: Timestamp.now(),
      lastActivity: Timestamp.now(),
      currentStep: 1,
      stepsVisited: [1],
      timePerStep: { 1: 0 },
      ...(leadSource && { leadSource }),  // Only include if leadSource exists
      completed: false,
      userAgent: navigator.userAgent || '',
      referrer: document.referrer || ''
    };
    
    await setDoc(doc(db, 'calculator_analytics', sessionId), sessionData);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Update analytics session with step progression (no PII)
 * @param sessionId Session identifier
 * @param currentStep Current step number
 * @param additionalData Optional non-PII data (industry, company size)
 */
export const updateAnalyticsSession = async (
  sessionId: string,
  currentStep: number,
  additionalData?: {
    industry?: string;
    companySize?: string;
    timeOnStep?: number;
  }
): Promise<void> => {
  try {
    if (!isFirebaseConfigured() || !sessionId) return;

    const updateData: Record<string, any> = {
      lastActivity: serverTimestamp(),
      currentStep,
      [`timePerStep.${currentStep}`]: additionalData?.timeOnStep || 0
    };
    
    // Only add non-PII data
    if (additionalData?.industry) {
      updateData.industry = additionalData.industry;
    }
    if (additionalData?.companySize) {
      updateData.companySize = additionalData.companySize;
    }
    
    await updateDoc(doc(db, 'calculator_analytics', sessionId), updateData);
    
    // Also update steps visited array
    const currentSteps = await getStepsVisited(sessionId);
    const uniqueSteps = Array.from(new Set([...currentSteps, currentStep]));
    await updateDoc(doc(db, 'calculator_analytics', sessionId), {
      stepsVisited: uniqueSteps
    });
  } catch (error) {
    console.error('Error updating analytics session:', error);
  }
};

/**
 * Helper to get steps visited (for array update)
 */
const getStepsVisited = async (sessionId: string): Promise<number[]> => {
  // In production, you'd fetch this from Firestore
  // For now, return empty array to avoid complexity
  return [];
};

/**
 * Save checkpoint at step completion (with user consent)
 * @param sessionId Session identifier
 * @param step Current step number
 * @param formData Current form data
 * @param calculations Current calculations if available
 * @param leadSource Optional lead source from URL parameter
 * @param isEditSession Whether this is an edit session
 */
export const saveCheckpoint = async (
  sessionId: string,
  step: number,
  formData: Partial<CalculatorFormData>,
  calculations?: Partial<CalculatorResults>,
  leadSource?: string,
  isEditSession?: boolean,
  originalReportId?: string
): Promise<boolean> => {
  try {
    if (!isFirebaseConfigured() || !sessionId) return false;

    // Only save checkpoints for users with email (no more root collection saves)
    if (!formData.contactEmail) {
      // No checkpoint saving for anonymous users
      return false;
    }
    
    // Get or create user record
    const userId = await createUserDocumentId(formData.contactEmail);
    const userRef = doc(db, 'users', userId);
    const sessionRef = doc(collection(userRef, 'calculator_sessions'), sessionId);
    
    // Get existing session to preserve startedAt
    const existingSession = await getDoc(sessionRef);
    const existingData = existingSession.exists() ? existingSession.data() : {};
    
    // Clean formData by removing undefined values (Firestore doesn't accept undefined)
    const cleanFormData = Object.entries(formData || {}).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    // Build checkpoint data with industry-standard timestamps
    const checkpoint: SessionCheckpoint & {
      createdAt?: any;  // Using any because serverTimestamp() returns FieldValue
      updatedAt?: any;  // Using any because serverTimestamp() returns FieldValue
      startedAt?: Timestamp;
      completedAt?: Timestamp;
    } = {
      sessionId,
      savedAt: Timestamp.now(),  // Keep for backward compatibility
      currentStep: step,
      highestStepReached: Math.max(step, existingData.highestStepReached || step), // Track highest step reached
      ...(leadSource && { leadSource }),  // Only include if leadSource exists
      ...(isEditSession && { isEditSession }),  // Only include if isEditSession is true
      ...(originalReportId && { originalReportId }),  // Only include if originalReportId exists
      formData: cleanFormData,  // Use cleaned formData without undefined values
      ...(formData.contactEmail && { consentGiven: Timestamp.now() }),  // Only include if email exists
      // Industry standard: always have createdAt and updatedAt
      createdAt: existingData.createdAt || serverTimestamp(),  // Preserve createdAt if exists
      updatedAt: serverTimestamp(),  // Always update on save
      startedAt: existingData.startedAt || existingData.createdAt || Timestamp.now(), // Preserve or set startedAt
    };
    
    // Mark as completed if this is the final step (step 4)
    if (step === 4 && calculations) {
      checkpoint.completedAt = Timestamp.now();
    }
    
    // Include contact info only if provided
    if (formData.organizationName || formData.contactName || formData.contactEmail) {
      checkpoint.contactInfo = {
        organizationName: formData.organizationName || '',
        contactName: formData.contactName || '',
        contactEmail: formData.contactEmail || '',
        contactPhone: formData.contactPhone || '',
        contactTitle: formData.contactTitle || ''
      };
    }
    
    // Include calculations if available
    if (calculations && Object.keys(calculations).length > 0) {
      checkpoint.calculations = calculations;
    }
    
    // Save to user's session collection only
    await setDoc(sessionRef, checkpoint, { merge: true });
    return true;
  } catch (error) {
    console.error('Error saving checkpoint:', error);
    return false;
  }
};

/**
 * Mark session as completed
 * @param sessionId Session identifier
 * @param completedAtStep Optional step number where completion occurred (3 for TAS, 4 for ROI)
 * @param userEmail Optional user email to update session document
 */
export const markSessionCompleted = async (
  sessionId: string,
  completedAtStep?: number,
  userEmail?: string
): Promise<void> => {
  try {
    if (!isFirebaseConfigured() || !sessionId) return;

    // Update analytics collection
    await updateDoc(doc(db, 'calculator_analytics', sessionId), {
      completed: true,
      completedAt: serverTimestamp(),
      ...(completedAtStep && { currentStep: completedAtStep, highestStepReached: completedAtStep })
    });

    // Also update the user's session document if we have the email
    if (userEmail && completedAtStep) {
      try {
        const userId = await createUserDocumentId(userEmail);
        const sessionRef = doc(db, 'users', userId, 'calculator_sessions', sessionId);

        // Check if session exists in user collection
        const sessionDoc = await getDoc(sessionRef);
        if (sessionDoc.exists()) {
          await updateDoc(sessionRef, {
            completedAt: serverTimestamp(),
            currentStep: completedAtStep,
            highestStepReached: completedAtStep
          });
        }
      } catch (userSessionError) {
        // Log but don't fail if user session update fails
        console.warn('Could not update user session document:', userSessionError);
      }
    }
  } catch (error) {
    console.error('Error marking session as completed:', error);
  }
};

// ============================================
// NEW USER-CENTRIC FUNCTIONS
// ============================================

/**
 * Get or create a user record based on email
 * @param email User's email address
 * @param profileData Additional profile data to update
 * @returns User document reference
 */
export const getOrCreateUserRecord = async (
  email: string,
  profileData?: Partial<UserProfile>
) => {
  try {
    if (!isFirebaseConfigured()) {
      throw new Error('Firebase not configured');
    }
    
    const userId = await createUserDocumentId(email);
    const userRef = doc(db, 'users', userId);
    
    // Check if user exists
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Create new user record
      const newUserProfile: UserProfile = {
        contactEmail: email,
        organizationName: profileData?.organizationName || '',
        contactName: profileData?.contactName || '',
        contactTitle: profileData?.contactTitle || '',
        contactPhone: profileData?.contactPhone || '',
        firstSeen: Timestamp.now(),
        lastActive: Timestamp.now(),
        totalSessions: 0
      };
      
      await setDoc(userRef, { profile: newUserProfile });
    } else {
      // Update existing user's last active time and profile
      const updates: any = {
        'profile.lastActive': serverTimestamp()
      };
      
      if (profileData?.organizationName) {
        updates['profile.organizationName'] = profileData.organizationName;
      }
      if (profileData?.contactName) {
        updates['profile.contactName'] = profileData.contactName;
      }
      if (profileData?.contactTitle) {
        updates['profile.contactTitle'] = profileData.contactTitle;
      }
      if (profileData?.contactPhone) {
        updates['profile.contactPhone'] = profileData.contactPhone;
      }
      
      await updateDoc(userRef, updates);
    }
    
    return userRef;
  } catch (error) {
    console.error('Error in getOrCreateUserRecord:', error);
    throw error;
  }
};

/**
 * Save a calculator session under the user's record
 * @param email User's email address
 * @param sessionId Existing session ID to use
 * @param formData Calculator form data
 * @param results Calculator results
 * @param enhancedData Additional enhanced data including all calculation details
 * @param leadSource Optional lead source from URL parameter
 * @returns Session reference and ID
 */
export const saveUserCalculatorSession = async (
  email: string,
  sessionId: string,
  formData: CalculatorFormData,
  results: CalculatorResults,
  enhancedData?: CalculatorSession['enhancedData'] & {
    componentData?: any[];  // Chart component data
    improvementData?: any[];  // Improvement suggestions
    tasComponents?: any;  // TAS component scores
    topPerformerTAS?: number;
    industryAverageTAS?: number;
    // PDF fields removed - using HTML reports only
  },
  leadSource?: string
): Promise<{ userRef: any; sessionRef: any; sessionId: string } | null> => {
  try {
    if (!isFirebaseConfigured()) {
      // Firebase not configured, skipping session save
      return null;
    }
    
    // Get or create user record
    const userRef = await getOrCreateUserRecord(email, {
      organizationName: formData.organizationName,
      contactName: formData.contactName,
      contactTitle: formData.contactTitle,
      contactPhone: formData.contactPhone
    });
    
    // Increment total sessions counter
    await updateDoc(userRef, {
      'profile.totalSessions': (await getDoc(userRef)).data()?.profile?.totalSessions + 1 || 1
    });
    
    // Use the provided sessionId to create the session document
    const sessionRef = doc(collection(userRef, 'calculator_sessions'), sessionId);
    
    // Clean formData by removing undefined values (Firestore doesn't accept undefined)
    const cleanFormData = Object.entries(formData || {}).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    
    // Store COMPLETE calculation state for single source of truth
    const sessionData: CalculatorSession & { 
      calculationSnapshot?: any;
      // PDF fields removed
      finalizedAt?: Timestamp;
      createdAt?: any;  // Using any because serverTimestamp() returns FieldValue
      updatedAt?: any;  // Using any because serverTimestamp() returns FieldValue
    } = {
      sessionId,
      ...(leadSource && { leadSource }),  // Only include if leadSource exists
      // Industry standard timestamps
      createdAt: serverTimestamp(),  // Always set on creation
      updatedAt: serverTimestamp(),  // Always set on update
      // Domain-specific timestamps
      startedAt: Timestamp.now(),
      completedAt: Timestamp.now(),
      finalizedAt: Timestamp.now(),  // Mark when report was finalized
      formData: cleanFormData,  // Use cleaned formData
      results,
      // PDF fields removed - reports are HTML only
      scenarioMode: enhancedData?.enhancedSavings ? 'moderate' : undefined,
      version: '1.0.0', // Update this when calculator version changes
      enhancedData,
      // Store complete calculation snapshot for consistency (clean undefined values)
      calculationSnapshot: Object.fromEntries(
        Object.entries({
          componentData: enhancedData?.componentData || [],
          improvementData: enhancedData?.improvementData || [],
          tasComponents: enhancedData?.tasComponents || results.tasComponents,
          topPerformerTAS: enhancedData?.topPerformerTAS,
          industryAverageTAS: enhancedData?.industryAverageTAS,
          currentTAS: results.currentTAS,
          projectedTAS: results.projectedTAS,
          totalAnnualSavings: results.savings?.totalAnnualSavings,
          annualRoi: results.annualRoi,
          paybackPeriod: enhancedData?.paybackPeriod,
          year1ROI: enhancedData?.year1ROI,
          steadyStateROI: enhancedData?.steadyStateROI
        }).filter(([_, value]) => value !== undefined)
      )
    };
    
    await setDoc(sessionRef, sessionData);
    
    
    return { userRef, sessionRef, sessionId };
  } catch (error) {
    console.error('Error saving user calculator session:', error);
    return null;
  }
};

/**
 * Get user's calculator history
 * @param email User's email address
 * @param limitCount Number of sessions to retrieve
 * @returns Array of calculator sessions
 */
export const getUserCalculatorHistory = async (
  email: string,
  limitCount: number = 10
): Promise<CalculatorSession[]> => {
  try {
    if (!isFirebaseConfigured()) {
      return [];
    }
    
    const userId = await createUserDocumentId(email);
    const userRef = doc(db, 'users', userId);
    const sessionsRef = collection(userRef, 'calculator_sessions');
    
    const q = query(
      sessionsRef,
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      ...doc.data() as CalculatorSession,
      // Convert Firestore timestamps to JS Dates for easier handling
      startedAt: doc.data().startedAt,
      completedAt: doc.data().completedAt
    }));
  } catch (error) {
    console.error('Error fetching user calculator history:', error);
    return [];
  }
};

// PDF update function removed - using HTML reports only

/**
 * Get a specific calculator session
 * @param email User's email address
 * @param sessionId Session ID
 * @returns Calculator session data
 */
export const getUserSession = async (
  email: string,
  sessionId: string
): Promise<CalculatorSession | null> => {
  try {
    if (!isFirebaseConfigured()) return null;
    
    const userId = await createUserDocumentId(email);
    const userRef = doc(db, 'users', userId);
    const sessionRef = doc(collection(userRef, 'calculator_sessions'), sessionId);
    
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      return sessionDoc.data() as CalculatorSession;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user session:', error);
    return null;
  }
};

/**
 * Update session email metadata after email is sent
 * @param email User's email address
 * @param sessionId Session ID
 * @param emailRecipients Array of email recipients (including CC)
 * @returns Success status
 */
export const updateSessionEmailMetadata = async (
  email: string,
  sessionId: string,
  emailRecipients: string[]
): Promise<boolean> => {
  try {
    if (!isFirebaseConfigured() || !email || !sessionId) return false;
    
    const userId = await createUserDocumentId(email);
    const userRef = doc(db, 'users', userId);
    const sessionRef = doc(collection(userRef, 'calculator_sessions'), sessionId);
    
    // Check if session exists
    const sessionDoc = await getDoc(sessionRef);
    if (!sessionDoc.exists()) {
      console.error('Session not found:', sessionId);
      return false;
    }
    
    // Get existing email recipients and merge with new ones
    const existingData = sessionDoc.data();
    const existingRecipients = existingData.emailRecipients || [];
    
    // Combine and deduplicate recipients using filter approach for compatibility
    const combined = [...existingRecipients, ...emailRecipients];
    const allRecipients = combined.filter((email, index) => combined.indexOf(email) === index);
    
    // Update session with email metadata
    await updateDoc(sessionRef, {
      emailRecipients: allRecipients,
      emailsSent: (existingData.emailsSent || 0) + 1,
      lastEmailSent: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Also update metadata field for admin console compatibility
    await updateDoc(sessionRef, {
      'metadata.emailRecipients': allRecipients,
      'metadata.lastEmailSent': serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating session email metadata:', error);
    return false;
  }
};

/**
 * Track when a user returns to the calculator
 * @param sessionId Session ID
 * @param userId User ID
 * @param isEdit Whether user is editing the session
 * @returns Promise with success status
 */
export const trackCalculatorReturn = async (
  sessionId: string,
  userId?: string,
  isEdit: boolean = false
): Promise<boolean> => {
  try {
    const isConfigured = isFirebaseConfigured();
    if (!isConfigured) return false;

    // Update analytics collection
    const analyticsRef = doc(db, 'calculator_analytics', sessionId);
    const analyticsDoc = await getDoc(analyticsRef);

    if (analyticsDoc.exists()) {
      const currentData = analyticsDoc.data();
      const updates: any = {
        returnCount: (currentData.returnCount || 0) + 1,
        lastReturnedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (isEdit) {
        updates.editCount = (currentData.editCount || 0) + 1;
      }

      await updateDoc(analyticsRef, updates);
    }

    // Update user session if userId is provided
    if (userId) {
      const sessionRef = doc(db, 'users', userId, 'calculator_sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (sessionDoc.exists()) {
        const currentData = sessionDoc.data();
        const updates: any = {
          returnCount: (currentData.returnCount || 0) + 1,
          lastReturnedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        if (isEdit) {
          updates.editCount = (currentData.editCount || 0) + 1;
        }

        await updateDoc(sessionRef, updates);
      }
    }

    return true;
  } catch (error) {
    console.error('Error tracking calculator return:', error);
    return false;
  }
};

/**
 * Track time spent in calculator session
 * @param sessionId Session ID
 * @param userId User ID
 * @param timeSpent Time spent in seconds
 * @returns Promise with success status
 */
export const trackTimeSpent = async (
  sessionId: string,
  timeSpent: number,
  userId?: string
): Promise<boolean> => {
  try {
    const isConfigured = isFirebaseConfigured();
    if (!isConfigured) return false;

    // Update analytics collection
    const analyticsRef = doc(db, 'calculator_analytics', sessionId);
    const analyticsDoc = await getDoc(analyticsRef);

    if (analyticsDoc.exists()) {
      const currentData = analyticsDoc.data();
      await updateDoc(analyticsRef, {
        totalTimeSpent: (currentData.totalTimeSpent || 0) + timeSpent,
        updatedAt: serverTimestamp()
      });
    }

    // Update user session if userId is provided
    if (userId) {
      const sessionRef = doc(db, 'users', userId, 'calculator_sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (sessionDoc.exists()) {
        const currentData = sessionDoc.data();
        await updateDoc(sessionRef, {
          totalTimeSpent: (currentData.totalTimeSpent || 0) + timeSpent,
          updatedAt: serverTimestamp()
        });
      }
    }

    return true;
  } catch (error) {
    console.error('Error tracking time spent:', error);
    return false;
  }
};